/**
 * Écran de connexion (composition ScreenLogin du design) : LoginPanel + NewsPanel dans .ag-login-screen.
 * SSO PRODEVAL (Entra ID via Cognito) en principal ; dépliant « Recevoir un code par e-mail » (secours pour tous).
 * Aucun mot de passe : les utilisateurs n'en ont pas (décision du 22/10/2026).
 * Fond du panneau d'actualité : diaporama de photos d'installations PRODEVAL (04/11/2026, PhotoSlideshow) ; son bouton
 * pause arrête aussi le défilement des actualités.
 */
import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LANGUES, type Langue } from '@agora/shared';
import { Button, Icon, LocaleSwitcher, LoginPanel, Menu, NewsPanel, PhotoSlideshow, TextField } from '../../design';
import { useConfig } from '../contexte';
import { useT } from '../i18n';
import { useSession, type Jetons } from '../auth/session';
import { ErreurConnexion, demanderCode, lancerSso, validerCode } from '../auth/cognito';
import { ACTUALITES, actualitesDatees } from '../donnees-exemple';
import { LogoProdeval } from '../shell/BarreLaterale';
import { PHOTOS_CONNEXION, TAILLES_PHOTOS_CONNEXION } from './photos-connexion';

const CLE_DESTINATION = 'agora.apresConnexion';

/** Mémorise la page demandée avant la connexion (retour après connexion). */
export function memoriserDestination(chemin: string) {
  try { if (chemin && !chemin.startsWith('/connexion') && !chemin.startsWith('/auth')) sessionStorage.setItem(CLE_DESTINATION, chemin); } catch { /* ignoré */ }
}
export function destinationApresConnexion(): string {
  try {
    const d = sessionStorage.getItem(CLE_DESTINATION);
    sessionStorage.removeItem(CLE_DESTINATION);
    return d || '/';
  } catch { return '/'; }
}

type Etape = 'email' | 'code';
type Message = { type: 'erreur' | 'info'; texte: string };

export function Message({ message }: { message: Message | null }) {
  if (!message) return null;
  return (
    <div className={message.type === 'erreur' ? 'agx-message' : 'agx-message agx-message-info'} role={message.type === 'erreur' ? 'alert' : 'status'}>
      <Icon name={message.type === 'erreur' ? 'bell' : 'spark'} size={16} />
      <span>{message.texte}</span>
    </div>
  );
}

export function EcranConnexion() {
  const config = useConfig();
  const { t, langue, locale, definirLangue } = useT();
  const { etat, ouvrir } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const erreurRetour = (location.state as { erreur?: ErreurConnexion['code'] } | null)?.erreur;

  const [deplie, setDeplie] = React.useState(!!erreurRetour && erreurRetour !== 'compteLie' ? false : false);
  const [etape, setEtape] = React.useState<Etape>('email');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [session, setSession] = React.useState('');
  const [occupe, setOccupe] = React.useState(false);
  const [message, setMessage] = React.useState<Message | null>(erreurRetour ? { type: 'erreur', texte: t(`connexion.erreurs.${erreurRetour}`) } : null);
  const [erreurChamp, setErreurChamp] = React.useState<string | undefined>();
  const [actualite, setActualite] = React.useState(0);
  const [pause, setPause] = React.useState(false);

  React.useEffect(() => {
    if (pause) return undefined;
    const id = window.setInterval(() => setActualite((i) => (i + 1) % ACTUALITES.length), 9000);
    return () => window.clearInterval(id);
  }, [pause]);

  if (etat === 'connecte') return <Navigate to="/" replace />;

  const reussite = (j: Jetons) => {
    ouvrir(j);
    navigate(destinationApresConnexion(), { replace: true });
  };
  const maquette = async (adresse: string) => {
    (await import('../api/maquette')).enregistrerMoiMaquette(adresse || 'vincent.barbier@prodeval.com');
    reussite({ idToken: 'maquette', accessToken: 'maquette', expireA: Date.now() + 365 * 86_400_000, origine: 'maquette' });
  };
  const echec = (e: unknown) => {
    const c = e instanceof ErreurConnexion ? e.code : 'generique';
    setMessage({ type: 'erreur', texte: t(`connexion.erreurs.${c}`) });
  };
  const emailValide = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const sso = async () => {
    setMessage(null);
    if (!config.connexion.sso) { setMessage({ type: 'info', texte: t('connexion.ssoIndisponible') }); return; }
    if (config.maquette) { void maquette(''); return; }
    setOccupe(true);
    try { sessionStorage.removeItem('agora.ssoRelance'); await lancerSso(config); } catch (e) { setOccupe(false); echec(e); }
  };

  const soumettre = async () => {
    setMessage(null);
    setErreurChamp(undefined);
    if (etape === 'email' && !emailValide(email)) { setErreurChamp(t('connexion.emailExemple')); return; }
    if (etape === 'code' && !code.trim()) return;
    setOccupe(true);
    try {
      if (config.maquette) {
        if (etape === 'email') { setEtape('code'); setSession('maquette'); } else void maquette(email);
      } else if (etape === 'email') {
        setSession(await demanderCode(config, email));
        setEtape('code');
      } else {
        const r = await validerCode(config, email, session, code);
        if (r.jetons) { reussite(r.jetons); return; }
        if (r.session) setSession(r.session);
        setMessage({ type: 'erreur', texte: t('connexion.erreurs.codeInvalide') });
      }
    } catch (e) {
      echec(e);
    }
    setOccupe(false);
  };

  const lien = (libelle: string, action: () => void) => (
    <button type="button" className="ag-login-link agx-lien" onClick={action}>{libelle}</button>
  );

  let champs: React.ReactNode;
  if (!config.connexion.secoursEmail) {
    // Envoi d'e-mails pas encore activé (identité SES non vérifiée) : seul le SSO est possible.
    champs = <Message message={{ type: 'info', texte: t('connexion.secoursIndisponible') }} />;
  } else if (etape === 'code') {
    champs = (
      <>
        <p className="agx-aide">{t('connexion.codeAide', { email })}</p>
        <TextField label={t('connexion.code')} type="text" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={setCode} maxLength={8} autoFocus />
        <div className="ag-login-row">
          {lien(t('connexion.renvoyer'), () => { setEtape('email'); setCode(''); void soumettreEmailDeNouveau(); })}
          {lien(t('connexion.changerAdresse'), () => { setEtape('email'); setCode(''); setMessage(null); })}
        </div>
        <button type="submit" className="ag-btn ag-btn-primary ag-btn-touch" disabled={occupe}>{t('connexion.seConnecter')}<Icon name="arrow" size={18} /></button>
      </>
    );
  } else {
    champs = (
      <>
        <TextField label={t('connexion.email')} type="email" autoComplete="username" inputMode="email" placeholder={t('connexion.emailExemple')} value={email} onChange={setEmail} error={erreurChamp} autoFocus />
        <button type="submit" className="ag-btn ag-btn-primary ag-btn-touch" disabled={occupe}>{t('connexion.envoyerCode')}<Icon name="arrow" size={18} /></button>
      </>
    );
  }

  async function soumettreEmailDeNouveau() {
    setMessage(null);
    if (config.maquette) { setEtape('code'); return; }
    setOccupe(true);
    try { setSession(await demanderCode(config, email)); setEtape('code'); } catch (e) { echec(e); }
    setOccupe(false);
  }

  const options = LANGUES.map((l) => ({ id: l, label: t(`langues.${l}`) }));

  return (
    <div className="ag-screen ag-canvas agx-plein">
      {/* Contenu principal (repère « main ») : la connexion vient en premier, aucun bloc à contourner, donc pas de
          lien d'évitement ici (il est dans la coquille de l'application connectée). */}
      <main className="ag-login-screen agx-connexion">
        <LoginPanel
          logo={<LogoProdeval hauteur={22} />}
          overline={t('connexion.bienvenue')}
          title={t('app.nom')}
          subtitle={t('app.slogan')}
          ssoLabel={t('connexion.sso')}
          ssoHint={t('connexion.ssoAide')}
          onSso={() => void sso()}
          ssoBusy={occupe && !deplie}
          message={<Message message={message} />}
          orLabel={t('connexion.ou')}
          classicLabel={t('connexion.secours')}
          expanded={deplie}
          onToggleClassic={() => { setDeplie((d) => !d); setMessage(null); }}
          form={champs}
          onSubmit={() => void soumettre()}
          emailLabel={t('connexion.email')} passwordLabel={t('connexion.code')} rememberLabel="" forgotLabel="" submitLabel={t('connexion.seConnecter')}
          footer={
            <>
              <div className="agx-pied-connexion">
                <span>{t('connexion.aide')}</span>
                <div className="agx-hote-menu">
                  {/* Menu déroulant Radix : au-dessus de la puce, aligné sur sa fin ; la langue courante est cochée. */}
                  <Menu
                    trigger={<LocaleSwitcher language={t(`langues.${langue}`)} ariaLabel={t('reglages.langue')} />}
                    side="top" align="end"
                    className="agx-menu-haut" ariaLabel={t('reglages.langue')} activeId={langue}
                    items={options}
                    onSelect={(id) => definirLangue(id as Langue)}
                  />
                </div>
              </div>
              <span>{t('connexion.rgpd')}</span>
              {config.maquette ? <span>{t('app.maquette')}</span> : config.env !== 'prod' ? <span>{t('app.environnement', { env: config.env })}</span> : null}
            </>
          }
        />
        <NewsPanel
          overline={t('actualite.titre')} moreLabel={t('actualite.lireSuite')} nextLabel={t('actualite.aLireAussi')}
          index={actualite} onIndex={setActualite} items={actualitesDatees(locale, 'long')}
          art={
            <PhotoSlideshow
              photos={PHOTOS_CONNEXION} sizes={TAILLES_PHOTOS_CONNEXION}
              pauseLabel={t('actualite.pause')} playLabel={t('actualite.reprendre')}
              paused={pause} onPausedChange={setPause}
            />
          }
        />
      </main>
    </div>
  );
}

/** Retour du SSO Entra ID (/auth/retour) : échange du code, relance automatique après association du compte. */
export function RetourAuth() {
  const config = useConfig();
  const { t } = useT();
  const { ouvrir } = useSession();
  const navigate = useNavigate();
  const fait = React.useRef(false);
  React.useEffect(() => {
    if (fait.current) return;
    fait.current = true;
    const params = new URLSearchParams(window.location.search);
    import('../auth/cognito').then(({ terminerSso }) => terminerSso(config, params))
      .then((j) => {
        sessionStorage.removeItem('agora.ssoRelance');
        ouvrir(j);
        navigate(destinationApresConnexion(), { replace: true });
      })
      .catch(async (e: unknown) => {
        const code = e instanceof ErreurConnexion ? e.code : 'generique';
        // Premier SSO : Cognito vient d'associer le compte Entra au compte AGORA → relance unique et transparente.
        if (code === 'compteLie' && !sessionStorage.getItem('agora.ssoRelance')) {
          sessionStorage.setItem('agora.ssoRelance', '1');
          await lancerSso(config);
          return;
        }
        navigate('/connexion', { replace: true, state: { erreur: code } });
      });
  }, [config, navigate, ouvrir]);
  return (
    <div className="ag-screen ag-canvas agx-plein agx-centre">
      <Button variant="glass" disabled icon="key">{t('app.chargement')}</Button>
    </div>
  );
}
